"use client";

import { useEffect } from "react";

/**
 * Next.js App Router root error boundary — catches otherwise-uncaught render
 * errors that would previously white-screen with no report anywhere (this app
 * has no error.tsx and no Sentry). Must render its own <html>/<body>: it
 * replaces the root layout when it fires.
 *
 * ChunkLoadError (10/7d in PostHog $exception — the tab still has an old
 * deploy's bundle open, and the hashed chunk file it now asks for is gone)
 * gets a hard reload instead of the generic "Try again" button: `reset()`
 * alone just re-renders with the same stale bundle and fails again, forcing
 * the visitor to manually refresh. Guarded by a sessionStorage flag so a
 * genuinely broken chunk can't reload-loop forever.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error(error);
    const posthog = (window as unknown as { posthog?: { captureException?: (e: unknown) => void } })
      .posthog;
    posthog?.captureException?.(error);

    if (/ChunkLoadError|Loading chunk .* failed/i.test(`${error.name} ${error.message}`)) {
      const key = "cmk-chunk-reload";
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
      }
    }
  }, [error]);

  return (
    <html lang="en">
      <body className="flex min-h-screen flex-col items-center justify-center bg-white px-6 text-center font-sans text-neutral-900">
        <p className="text-sm font-semibold uppercase tracking-wide text-brand-600">
          changemaker
        </p>
        <h1 className="mt-3 text-2xl font-semibold">Something broke on our end</h1>
        <p className="mt-2 max-w-md text-neutral-600">
          The page hit an unexpected error. It&rsquo;s been reported — try again,
          or head back to the job board.
        </p>
        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            onClick={() => reset()}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Try again
          </button>
          <a
            href="/jobs"
            className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-semibold text-ink hover:bg-neutral-50"
          >
            Back to jobs
          </a>
        </div>
      </body>
    </html>
  );
}
